import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Show immediately - this is important for GDPR compliance
      setShowBanner(true);
    }
  }, []);

  const handleAccept = () => {
    localStorage.setItem('cookie-consent', 'accepted');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    
    // Dispatch storage event for Analytics component to detect
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookie-consent',
      newValue: 'accepted'
    }));
    
    console.log('All cookies accepted - Analytics enabled');
  };

  const handleDecline = () => {
    localStorage.setItem('cookie-consent', 'declined');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    
    // Dispatch storage event for Analytics component to detect
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookie-consent',
      newValue: 'declined'
    }));
    
    console.log('Cookies declined - Analytics disabled');
  };

  const handleAcceptEssential = () => {
    localStorage.setItem('cookie-consent', 'essential-only');
    localStorage.setItem('cookie-consent-date', new Date().toISOString());
    setShowBanner(false);
    
    // Dispatch storage event for Analytics component to detect
    window.dispatchEvent(new StorageEvent('storage', {
      key: 'cookie-consent',
      newValue: 'essential-only'
    }));
    
    console.log('Only essential cookies accepted - Analytics disabled');
  };

  if (!showBanner) return null;

  return (
    <>
      {/* Dark overlay that blocks everything - covers entire screen */}
      <div 
        className="fixed inset-0 bg-black/80 z-[99998] backdrop-blur-sm"
        style={{ pointerEvents: 'all' }}
        onClick={(e) => e.stopPropagation()}
        data-testid="cookie-overlay"
      />
      
      {/* Cookie consent modal - centered on screen */}
      <div 
        className="fixed inset-0 z-[99999] flex items-center justify-center p-4"
        style={{ pointerEvents: 'none' }}
        data-testid="cookie-consent-banner"
      >
        <div 
          className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
          style={{ pointerEvents: 'all' }}
        >
          {/* Header */}
          <div className="bg-turquoise-400 text-white px-6 py-4 rounded-t-2xl">
            <h2 className="text-2xl font-bold flex items-center gap-2">
              🍪 Cookie-Einstellungen
            </h2>
          </div>
          
          {/* Content */}
          <div className="p-6">
            <p className="text-gray-700 leading-relaxed mb-4">
              Wir verwenden Cookies und ähnliche Technologien, um Ihnen die bestmögliche Erfahrung 
              auf unserer Website zu bieten.
            </p>
            
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <h3 className="font-semibold text-gray-900 mb-2">Folgende Cookies werden verwendet:</h3>
              <ul className="space-y-2 text-sm text-gray-600">
                <li className="flex items-start gap-2">
                  <span className="text-green-500 font-bold">✓</span>
                  <span><strong>Notwendige Cookies:</strong> Für die grundlegende Funktion der Website (z.B. Login-Status, Cookie-Einstellungen)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-blue-500 font-bold">○</span>
                  <span><strong>Analyse-Cookies:</strong> Zur Verbesserung unserer Website (Google Analytics - nur mit Ihrer Zustimmung)</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-purple-500 font-bold">○</span>
                  <span><strong>Externe Inhalte:</strong> Für eingebettete Medien (Mixcloud Musik-Player)</span>
                </li>
              </ul>
            </div>
            
            <p className="text-sm text-gray-500 mb-6">
              Weitere Informationen finden Sie in unserer{' '}
              <Link 
                to="/datenschutz" 
                className="text-turquoise-500 hover:underline font-medium"
                onClick={(e) => {
                  // Allow navigation but keep banner visible
                  e.stopPropagation();
                }}
              >
                Datenschutzerklärung
              </Link>.
            </p>
            
            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={handleDecline}
                className="flex-1 px-5 py-3 text-gray-600 border-2 border-gray-300 rounded-xl hover:bg-gray-50 transition-colors font-medium"
                data-testid="cookie-decline-btn"
              >
                Alle ablehnen
              </button>
              <button
                onClick={handleAcceptEssential}
                className="flex-1 px-5 py-3 text-gray-700 border-2 border-turquoise-300 rounded-xl hover:bg-turquoise-50 transition-colors font-medium"
                data-testid="cookie-essential-btn"
              >
                Nur Notwendige
              </button>
              <button
                onClick={handleAccept}
                className="flex-1 px-5 py-3 bg-turquoise-400 text-white rounded-xl hover:bg-turquoise-500 transition-colors font-semibold shadow-lg"
                data-testid="cookie-accept-btn"
              >
                Alle akzeptieren
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CookieConsent;
